import React, { useState, useEffect } from 'react';
import { sendFeedback } from '../services/notionApi';
import { saveFeedbackLog, loadFeedbackLogs } from '../services/feedbackApi';

const CATEGORIES = [
  { id: 'request', label: '✨ 機能の要望' },
  { id: 'bug', label: '🐛 バグ報告' },
  { id: 'praise', label: '😊 よかったこと' },
  { id: 'other', label: '💬 その他' },
];

const HISTORY_KEY = 'ondoku_feedback_history';
const CATEGORY_LABEL = { request: '✨ 機能の要望', bug: '🐛 バグ報告', praise: '😊 よかったこと', other: '💬 その他' };

function loadHistory() {
  try {
    return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
  } catch { return []; }
}

function saveToHistory(item) {
  try {
    const history = loadHistory();
    history.unshift(item); // 新しいものを先頭に
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, 20))); // 最大20件
  } catch {}
}

/** 学年ラベルを返す（例: 小2, 未就学, null=不明） */
function gradeLabel(birth_year, birth_month) {
  if (!birth_year) return null;
  const now = new Date();
  const age = now.getFullYear() - birth_year -
    (now.getMonth() + 1 < (birth_month ?? 4) ? 1 : 0);
  const g = age - 6;
  if (g <= 0) return '未就学';
  if (g <= 6) return `小${g}`;
  return `中${g - 6}`;
}

export default function FeedbackForm({ onBack, onNav, currentChild, isGuest }) {
  const [category, setCategory] = useState('request');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error
  const [history, setHistory] = useState(loadHistory);

  // ログイン済みの場合はSupabaseから履歴を取得
  useEffect(() => {
    if (!isGuest) {
      loadFeedbackLogs().then(logs => {
        if (logs !== null) setHistory(logs);
      });
    }
  }, [isGuest]);

  const senderName  = currentChild?.name ?? null;
  const senderGrade = gradeLabel(currentChild?.birth_year, currentChild?.birth_month);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setStatus('sending');
    try {
      await sendFeedback({ category, message, senderName, senderGrade, isGuest: isGuest ?? true });
      if (!isGuest) {
        // ログイン済み → Supabaseに保存してから再取得
        await saveFeedbackLog({ category, message, childName: senderName });
        const logs = await loadFeedbackLogs();
        if (logs !== null) setHistory(logs);
      } else {
        // ゲスト → localStorageに保存
        const item = { id: Date.now(), date: new Date().toISOString(), category, message, senderName };
        saveToHistory(item);
        setHistory(loadHistory());
      }
      setStatus('done');
    } catch (e) {
      console.error('フィードバック送信エラー:', e);
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className="screen feedback-screen">
        <div className="feedback-done">
          <div className="feedback-done-icon">📬</div>
          <h2>送信完了！</h2>
          <p>メッセージを受け取りました。<br />ありがとうございます😊</p>
          <button className="btn-primary" onClick={onBack}>
            もどる
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen feedback-screen">
      <button className="btn-back" onClick={onBack}>← もどる</button>
      <h2 className="feedback-title">📬 メッセージを送る</h2>

      {/* スクロール可能エリア */}
      <div className="feedback-scroll-area">
        <p className="feedback-subtitle">
          ご意見・ご要望・バグ報告など<br />
          なんでもお知らせください！
        </p>

        {/* 送り主表示 */}
        {senderName && (
          <div className="feedback-sender">
            <span className="feedback-sender-label">おくりぬし：</span>
            <span className="feedback-sender-name">
              {currentChild?.avatar && `${currentChild.avatar} `}{senderName}
              {senderGrade && `・${senderGrade}`}
              {!isGuest && ' ✅'}
            </span>
          </div>
        )}

        {/* カテゴリ選択 */}
        <div className="feedback-category-row">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              className={`feedback-category-btn ${category === c.id ? 'active' : ''}`}
              onClick={() => setCategory(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* メッセージ入力 */}
        <textarea
          className="feedback-textarea"
          placeholder="ここにメッセージを入力してください"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={6}
          maxLength={500}
        />
        <div className="feedback-char-count">{message.length} / 500</div>

        {status === 'error' && (
          <p className="feedback-error">
            送信できませんでした。もう一度お試しください。
          </p>
        )}

        <button
          className="feedback-btn-send"
          onClick={handleSubmit}
          disabled={!message.trim() || status === 'sending'}
        >
          {status === 'sending' ? '⏳ おくっています…' : '📨 おくる'}
        </button>

        {/* 送信履歴 */}
        {history.length > 0 && (
          <div className="feedback-history">
            <h3 className="feedback-history-title">📋 おくったメッセージ</h3>
            {history.map((item) => {
              const dateStr = item.created_at || item.date; // Supabase or localStorage
              const name = item.child_name || item.senderName;
              return (
                <div key={item.id} className="feedback-history-item">
                  <div className="feedback-history-meta">
                    <span className="feedback-history-category">
                      {CATEGORY_LABEL[item.category] ?? item.category}
                      {name && <span className="feedback-history-sender"> · {name}</span>}
                    </span>
                    <span className="feedback-history-date">{new Date(dateStr).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <div className="feedback-history-message">{item.message}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}
